// @ts-nocheck
/**
 * Nexode Air · 自定义元素 `nexode-air-section`
 *
 * 依赖 GSAP + ScrollTrigger；区块顺序与 `sections/nexode-air.liquid` 一致。
 * 对外：`window.UgNexodeAirReveal`（init / refresh / defaults）。
 */
(function () {
  'use strict';

  const TAG_NAME = 'nexode-air-section';
  const FORM_SUBMIT_URL = 'https://ecf.cirkleinc.com/api/form-submit';
  const MOBILE_MQ = '(max-width: 1023.98px)';
  const AIR65_PIN_LAST_FRAME_INDEX = 117;
  const AIR65_FRAME_EPS = 0.002;
  const AIR65_SCRUB = true;
  const NA_SCROLL_REVEAL_START = 'top 70%';
  let nexodeAirRefreshRaf = 0;

  /** 合并到下一帧再 ScrollTrigger.refresh，避免同帧多次改布局导致重复计算。 */
  function scheduleNexodeAirScrollTriggerRefresh() {
    const ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger || typeof ScrollTrigger.refresh !== 'function') return;
    if (nexodeAirRefreshRaf) return;
    nexodeAirRefreshRaf = window.requestAnimationFrame(() => {
      nexodeAirRefreshRaf = 0;
      try {
        ScrollTrigger.refresh();
      } catch (_) {}
    });
  }

  const REVEAL_DEFAULTS = Object.freeze({
    y: 26,
    duration: 0.88,
    ease: 'power2.out',
    start: NA_SCROLL_REVEAL_START,
    stagger: 0.18
  });

  /** 静音 + playsInline；与 nexode-air-template.js 一致（iOS 内联自动播） */
  function naPrimeInlineVideoForAutoplay(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', '');
    } catch (_) {}
  }

  function naTryPlayInlineVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    naPrimeInlineVideoForAutoplay(video);
    const run = () => {
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    };
    if (video.readyState >= 2) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(run));
      return;
    }
    const once = () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(run));
    };
    video.addEventListener('loadeddata', once, { once: true });
    video.addEventListener('canplay', once, { once: true });
    try {
      video.load();
    } catch (_) {}
  }

  /** 不调用 load()，避免重置缓冲（KV 断点切换）；未就绪则等 loadeddata/canplay */
  function naPlayBufferedInlineVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    naPrimeInlineVideoForAutoplay(video);
    const run = () => {
      try {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    };
    const schedule = () => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(run));
    };
    if (video.readyState >= 2) {
      schedule();
      return;
    }
    const once = () => {
      schedule();
    };
    video.addEventListener('loadeddata', once, { once: true });
    video.addEventListener('canplay', once, { once: true });
  }

  if (customElements.get(TAG_NAME)) {
    return;
  }

  /**
   * 滚动渐显：处理 `data-na-reveal` / `data-na-reveal-stagger`、时间线卡片与 Signup 卡、KV 邮箱图标；
   * Signup 卡内子节点不参与 stagger；`prefers-reduced-motion` 时直接显示不播动画。
   */
  function initNexodeAirScrollReveal(root) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const registered = [];
    const markRevealTrigger = (st) => {
      if (!st || typeof st !== 'object') return;
      try {
        st.__naReveal = true;
      } catch (_) {}
    };

    if (!gsap || !ScrollTrigger) {
      return function noop() {};
    }

    gsap.registerPlugin(ScrollTrigger);

    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      root.querySelectorAll('[data-na-reveal], [data-na-reveal-stagger] [data-na-reveal]').forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.opacity = '1';
          el.style.transform = 'none';
        }
      });
      return function noop() {};
    }

    const fromVars = { opacity: 0, y: REVEAL_DEFAULTS.y };
    const { duration, ease, start, stagger } = REVEAL_DEFAULTS;

    root.querySelectorAll('[data-na-reveal-stagger]').forEach((staggerRoot) => {
      if (!(staggerRoot instanceof HTMLElement)) return;
      if (staggerRoot.closest('.ug-nexode-air__signup-card')) return;
      const children = staggerRoot.querySelectorAll(':scope > [data-na-reveal]');
      if (!children.length) return;

      gsap.set(children, fromVars);
      const tween = gsap.to(children, {
        opacity: 1,
        y: 0,
        duration,
        stagger,
        ease,
        scrollTrigger: {
          trigger: staggerRoot,
          start,
          toggleActions: 'play none none none'
        }
      });
      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    root.querySelectorAll('[data-na-reveal]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.closest('[data-na-reveal-stagger]')) return;
      if (el.closest('.ug-nexode-air__signup-card')) return;

      gsap.set(el, fromVars);
      const tween = gsap.to(el, {
        opacity: 1,
        y: 0,
        duration,
        ease,
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: 'play none none none'
        }
      });
      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    root.querySelectorAll('.ug-nexode-air__timeline').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;

      const card = section.querySelector('.ug-nexode-air__timeline-card');
      const visual = section.querySelector('.ug-nexode-air__timeline-visual');

      if (card instanceof HTMLElement) {
        gsap.set(card, { autoAlpha: 0, y: 32 });
      }
      if (visual instanceof HTMLElement) {
        gsap.set(visual, { autoAlpha: 0, y: 40 });
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: NA_SCROLL_REVEAL_START,
          once: true
        }
      });

      if (card instanceof HTMLElement) {
        tl.to(card, {
          autoAlpha: 1,
          y: 0,
          duration: 1.05,
          ease: 'power2.out'
        });
      }

      if (visual instanceof HTMLElement) {
        tl.to(
          visual,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.95,
            ease: 'power2.out'
          },
          card instanceof HTMLElement ? '+=0.26' : 0
        );
      }

      if (tl.scrollTrigger) {
        markRevealTrigger(tl.scrollTrigger);
        registered.push(tl.scrollTrigger);
      }
    });

    root.querySelectorAll('.ug-nexode-air__signup-card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      gsap.set(card, { autoAlpha: 0, y: 36 });
      const tween = gsap.to(card, {
        autoAlpha: 1,
        y: 0,
        duration: 0.95,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: card,
          start: NA_SCROLL_REVEAL_START,
          once: true
        }
      });

      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    root.querySelectorAll('.ug-nexode-air__signup-visual').forEach((visual) => {
      if (!(visual instanceof HTMLElement)) return;

      const trigger = visual.closest('.ug-nexode-air__signup-card') || visual;
      gsap.set(visual, { autoAlpha: 0, y: 36 });
      const tween = gsap.to(visual, {
        autoAlpha: 1,
        y: 0,
        duration: 0.95,
        ease: 'power2.out',
        scrollTrigger: {
          trigger,
          start: NA_SCROLL_REVEAL_START,
          once: true
        }
      });

      if (tween.scrollTrigger) {
        markRevealTrigger(tween.scrollTrigger);
        registered.push(tween.scrollTrigger);
      }
    });

    if (!reduced) {
      const emailIconFrom = { autoAlpha: 0, y: 26, scale: 0.992 };
      const emailIconEase = 'power3.out';
      const emailIconDuration = 0.88;
      const emailIconStart = NA_SCROLL_REVEAL_START;

      root.querySelectorAll('[data-na-email-input-icon]').forEach((icon) => {
        if (!(icon instanceof HTMLElement)) return;
        const trigger =
          icon.closest('.ug-nexode-air__email-row') ||
          icon.closest('.ug-nexode-air__email-form') ||
          root;
        gsap.set(icon, emailIconFrom);
        const tween = gsap.to(icon, {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: emailIconDuration,
          ease: emailIconEase,
          scrollTrigger: {
            trigger,
            start: emailIconStart,
            once: true
          }
        });
        if (tween.scrollTrigger) {
          markRevealTrigger(tween.scrollTrigger);
          registered.push(tween.scrollTrigger);
        }
      });
    } else {
      root.querySelectorAll('[data-na-email-input-icon]').forEach((icon) => {
        if (icon instanceof HTMLElement) {
          icon.style.opacity = '1';
          icon.style.transform = 'none';
        }
      });
    }

    scheduleNexodeAirScrollTriggerRefresh();

    return function cleanup() {
      registered.forEach((st) => {
        try {
          st.kill();
        } catch (_) {}
      });
      registered.length = 0;
    };
  }

  /** 动态插入节点后：先 kill 本 root 内带 `__naReveal` 的 ScrollTrigger，再重新 init 渐显。 */
  function refreshNexodeAirScrollReveal(root) {
    const ST = window.ScrollTrigger;
    if (!(root instanceof HTMLElement) || typeof ST === 'undefined') {
      return function noop() {};
    }
    ST.getAll().forEach((st) => {
      if (st.__naReveal && st.trigger && root.contains(/** @type {Node} */ (st.trigger))) {
        st.kill();
      }
    });
    return initNexodeAirScrollReveal(root);
  }

  /** 眉题扫光：写 `--na-text-reveal` 百分比，与 CSS mask 进度一致。 */
  function setNexodeAirTextReveal(el, progress) {
    if (!(el instanceof HTMLElement)) return;
    const safe = Math.max(0, Math.min(100, progress));
    el.style.setProperty('--na-text-reveal', safe + '%');
  }

  /** Tab 激活底色 → 计算标签文字色（亮度阈值 0.62，避免浅底白字看不清）。 */
  function nexodeAirTabLabelColorForBg(bg) {
    if (!bg || typeof bg !== 'string') return '#ffffff';
    const s = bg.trim();
    let r = 133;
    let g = 134;
    let b = 148;
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
    if (hex) {
      let h = hex[1];
      if (h.length === 3) {
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      }
      r = parseInt(h.slice(0, 2), 16);
      g = parseInt(h.slice(2, 4), 16);
      b = parseInt(h.slice(4, 6), 16);
    } else if (/^rgba?\(/i.test(s)) {
      const m = s.match(/\d+(\.\d+)?/g);
      if (m && m.length >= 3) {
        r = Math.min(255, Math.round(Number(m[0])));
        g = Math.min(255, Math.round(Number(m[1])));
        b = Math.min(255, Math.round(Number(m[2])));
      }
    }
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.62 ? '#191a1a' : '#ffffff';
  }

  /** 帧资源 URL：prefix + 补零序号 + 扩展名。 */
  function buildNexodeAirFrameUrl(prefix, index, pad, ext) {
    return prefix + String(index).padStart(pad, '0') + '.' + ext;
  }

  /**
   * Highlight：拆字后逐字 GSAP 入场；整段共一条横向渐变，由 JS 写 `--na-hl-grad-w` 与每字 `--na-hl-grad-x`；
   * resize / ResizeObserver / ScrollTrigger.refresh 时重算字偏移，避免换行后渐变错位。
   */
  function initWhenNexode65wHighlight(root, ctx) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const { cleanups, registered, reduced } = ctx;

    root.querySelectorAll('[data-na-highlight]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const text = section.querySelector('[data-na-highlight-text]');
      if (!(text instanceof HTMLElement)) return;
      const wrap = text.querySelector('.ug-nexode-air__gradient-text-wrap');
      if (!(wrap instanceof HTMLElement)) return;

      const baseEl = wrap.querySelector('.ug-nexode-air__gradient-text-base');
      if (!(baseEl instanceof HTMLElement)) return;
      const fillEl = wrap.querySelector('.ug-nexode-air__gradient-text-fill');
      if (!(fillEl instanceof HTMLElement)) return;

      const plain = (baseEl.textContent || '').trim();
      if (!plain) return;

      if (!gsap || !ScrollTrigger || reduced) {
        text.style.opacity = '1';
        text.style.transform = 'none';
        return;
      }

      const savedWrapHtml = wrap.innerHTML;

      /**
       * 拆字动画：连续字母包在 .ug-nexode-air__highlight-word（nowrap）内，换行只发生在空格处；
       * 空格用文本节点；否则逐字 inline-block 会在词中间断行（手机端 MIG|HTY）。
       */
      const mountHighlightCharPairs = () => {
        wrap.innerHTML = '';
        const out = [];
        let i = 0;
        while (i < plain.length) {
          const ch = plain[i];
          if (ch === '\n' || ch === '\r') {
            i += 1;
            continue;
          }
          if (/\s/.test(ch)) {
            wrap.appendChild(document.createTextNode(ch === '\t' ? ' ' : ch));
            i += 1;
            continue;
          }
          const wordEl = document.createElement('span');
          wordEl.className = 'ug-nexode-air__highlight-word';
          while (i < plain.length && !/\s/.test(plain[i])) {
            const c = plain[i];
            const pair = document.createElement('span');
            pair.className = 'ug-nexode-air__highlight-char-pair';
            const b = document.createElement('span');
            b.className = 'ug-nexode-air__gradient-text-base ug-nexode-air__highlight-char-base';
            b.textContent = c;
            const f = document.createElement('span');
            f.className = 'ug-nexode-air__gradient-text-fill ug-nexode-air__highlight-char-fill';
            f.setAttribute('aria-hidden', 'true');
            f.textContent = c;
            pair.appendChild(b);
            pair.appendChild(f);
            wordEl.appendChild(pair);
            out.push(pair);
            i += 1;
          }
          if (wordEl.childElementCount > 0) {
            wrap.appendChild(wordEl);
          }
        }
        return out;
      };

      const pairs = mountHighlightCharPairs();
      if (!pairs.length) {
        wrap.innerHTML = savedWrapHtml;
        return;
      }

      const applyHighlightUnifiedGradientShifts = () => {
        if (!pairs.length) return;
        const wrapRect = wrap.getBoundingClientRect();
        const totalW = Math.max(1, Math.round(wrapRect.width));
        wrap.style.setProperty('--na-hl-grad-w', `${totalW}px`);
        pairs.forEach((pair) => {
          if (pair instanceof HTMLElement) {
            const pr = pair.getBoundingClientRect();
            const x = pr.left - wrapRect.left;
            pair.style.setProperty('--na-hl-grad-x', `${-Math.round(x)}px`);
          }
        });
      };

      applyHighlightUnifiedGradientShifts();
      window.requestAnimationFrame(() => {
        applyHighlightUnifiedGradientShifts();
        window.requestAnimationFrame(applyHighlightUnifiedGradientShifts);
      });
      const onHlGradResize = () => applyHighlightUnifiedGradientShifts();
      window.addEventListener('resize', onHlGradResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onHlGradResize));
      let hlGradRo = null;
      if (typeof ResizeObserver === 'function') {
        hlGradRo = new ResizeObserver(() => applyHighlightUnifiedGradientShifts());
        hlGradRo.observe(wrap);
        cleanups.push(() => {
          try {
            hlGradRo.disconnect();
          } catch (_) {}
        });
      }

      const restoreWrap = () => {
        wrap.innerHTML = savedWrapHtml;
      };
      cleanups.push(restoreWrap);

      gsap.set(pairs, { y: '-0.85em', autoAlpha: 0, transformOrigin: '50% 0%' });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: NA_SCROLL_REVEAL_START,
          toggleActions: 'play none none none'
        }
      });
      tl.fromTo(
        pairs,
        { y: '-0.85em', autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.68,
          ease: 'back.out(1.35)',
          stagger: {
            each: 0.038,
            from: 'start',
            ease: 'sine.inOut'
          },
          onComplete: () => {
            applyHighlightUnifiedGradientShifts();
          }
        }
      );

      if (tl.scrollTrigger) {
        registered.push(tl.scrollTrigger);
      }

      if (ScrollTrigger && typeof ScrollTrigger.addEventListener === 'function') {
        ScrollTrigger.addEventListener('refresh', applyHighlightUnifiedGradientShifts);
        cleanups.push(() => {
          ScrollTrigger.removeEventListener('refresh', applyHighlightUnifiedGradientShifts);
        });
      }
    });
  }

  /**
   * Air65：桌面 pin 内 canvas 按滚动 scrub 帧序列，进度在 `AIR65_PIN_LAST_FRAME_INDEX` 前后拆成两段 ST 防衔接闪帧；
   * 手机若配置 mo 视频则隐藏序列帧、播视频并单独做文案 ScrollTrigger 入场。
   */
  function initWhenNexode65wAir65(root, ctx) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const { cleanups, registered, reduced } = ctx;

    root.querySelectorAll('[data-na-air65]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const pin = section.querySelector('.ug-nexode-air__air65-pin');
      const stage = section.querySelector('.ug-nexode-air__air65-stage');
      const copy = section.querySelector('[data-na-air65-copy]');
      const eyebrow = section.querySelector('[data-na-air65-eyebrow]');
      const canvas = section.querySelector('[data-na-air65-canvas]');
      const poster = section.querySelector('[data-na-air65-poster]');
      const moVideoEl = section.querySelector('[data-na-air65-mo-video]');
      const prefix = section.dataset.framePrefix || '';
      const count = Number.parseInt(section.dataset.frameCount || '0', 10);
      const padW = Number.parseInt(section.dataset.framePad || '3', 10);
      const ext = section.dataset.frameExt || 'webp';
      const copyLineSelector =
        '.ug-nexode-air__air65-eyebrow, .ug-nexode-air__air65-title, .ug-nexode-air__air65-desc';
      const copyLines =
        copy instanceof HTMLElement
          ? Array.from(copy.querySelectorAll(copyLineSelector)).filter((el) => el instanceof HTMLElement)
          : [];

      const isAir65Mobile =
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
      const moVideoUrl = (section.dataset.air65MoVideo || '').trim();

      const setupAir65CopyEnter = () => {
        if (!gsap) return;
        if (copy instanceof HTMLElement) {
          gsap.set(copy, { autoAlpha: 1, y: 0 });
        }
        if (copyLines.length) {
          gsap.set(copyLines, { autoAlpha: 0, y: 28 });
          const enterTl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: NA_SCROLL_REVEAL_START,
              once: true
            }
          });
          enterTl.to(copyLines, {
            autoAlpha: 1,
            y: 0,
            duration: isAir65Mobile ? 1.12 : 0.92,
            stagger: isAir65Mobile ? 0.28 : 0.22,
            ease: 'power2.out'
          });
          if (eyebrow instanceof HTMLElement && !reduced) {
            enterTl.to(
              eyebrow,
              {
                '--na-text-reveal': '100%',
                duration: 1.15,
                ease: 'power2.inOut'
              },
              '<'
            );
          }
          if (enterTl.scrollTrigger) {
            registered.push(enterTl.scrollTrigger);
          }
        }
      };

      if (isAir65Mobile && moVideoUrl && moVideoEl instanceof HTMLVideoElement) {
        if (!(pin instanceof HTMLElement) || !(stage instanceof HTMLElement)) return;

        moVideoEl.loop = false;
        naPrimeInlineVideoForAutoplay(moVideoEl);
        try {
          moVideoEl.pause();
          moVideoEl.currentTime = 0;
        } catch (_) {}

        // 无 GSAP 时：进视口后 CSS 淡入再 play（与 ScrollTrigger 语义对齐，对齐 template）
        const setupMoVideoIoFallback = () => {
          moVideoEl.classList.add('ug-nexode-air__air65-mo-video--io-reveal');
          if (typeof IntersectionObserver === 'undefined') {
            moVideoEl.classList.add('is-visible');
            window.setTimeout(() => naTryPlayInlineVideo(moVideoEl), 480);
            return;
          }
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((ent) => {
                if (!ent.isIntersecting) return;
                io.disconnect();
                moVideoEl.classList.add('is-visible');
                window.setTimeout(() => naTryPlayInlineVideo(moVideoEl), 500);
              });
            },
            { threshold: 0.14, rootMargin: '0px 0px -6% 0px' }
          );
          io.observe(moVideoEl);
          cleanups.push(() => {
            try {
              io.disconnect();
            } catch (_) {}
          });
        };

        if (gsap && ScrollTrigger) {
          gsap.set(moVideoEl, { autoAlpha: 0 });
          const fadeDur = reduced ? 0 : 0.58;
          const videoEnterTl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: NA_SCROLL_REVEAL_START,
              once: true
            }
          });
          videoEnterTl.to(moVideoEl, {
            autoAlpha: 1,
            duration: fadeDur,
            ease: 'power2.out'
          });
          videoEnterTl.add(() => naTryPlayInlineVideo(moVideoEl));
          if (videoEnterTl.scrollTrigger) {
            registered.push(videoEnterTl.scrollTrigger);
          }
        } else {
          setupMoVideoIoFallback();
        }

        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTextReveal(eyebrow, reduced ? 100 : 0);
        }

        if (!gsap || !ScrollTrigger || reduced) {
          copyLines.forEach((el) => {
            el.style.opacity = '1';
            el.style.visibility = 'visible';
            el.style.transform = 'none';
          });
          if (eyebrow instanceof HTMLElement) {
            setNexodeAirTextReveal(eyebrow, 100);
          }
          if (copy instanceof HTMLElement) {
            copy.style.opacity = '';
            copy.style.visibility = '';
            copy.style.transform = '';
          }
          return;
        }

        setupAir65CopyEnter();
        return;
      }

      if (
        !(pin instanceof HTMLElement) ||
        !(stage instanceof HTMLElement) ||
        !(canvas instanceof HTMLCanvasElement) ||
        !prefix ||
        !count
      ) {
        return;
      }

      const ctx2d = canvas.getContext('2d');
      if (!ctx2d) return;

      const isAir65MobileFramesOnly = isAir65Mobile && !moVideoUrl;
      const frameLoadCount = isAir65MobileFramesOnly ? 1 : count;

      const frames = [];
      const imageSequence = { frame: 0 };

      const resolveRenderableFrame = (targetIndex) => {
        const safeIndex = Math.min(count - 1, Math.max(0, targetIndex));
        const direct = frames[safeIndex];
        if (direct && direct.complete && direct.naturalWidth) {
          return direct;
        }
        for (let prev = safeIndex - 1; prev >= 0; prev -= 1) {
          const prevFrame = frames[prev];
          if (prevFrame && prevFrame.complete && prevFrame.naturalWidth) {
            return prevFrame;
          }
        }
        for (let next = safeIndex + 1; next < count; next += 1) {
          const nextFrame = frames[next];
          if (nextFrame && nextFrame.complete && nextFrame.naturalWidth) {
            return nextFrame;
          }
        }
        return null;
      };

      const applyCanvasBufferSize = () => {
        const w = stage.offsetWidth;
        const h = stage.offsetHeight;
        if (w <= 0 || h <= 0) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const bw = Math.max(1, Math.round(w * dpr));
        const bh = Math.max(1, Math.round(h * dpr));
        if (canvas.width !== bw || canvas.height !== bh) {
          canvas.width = bw;
          canvas.height = bh;
        }
      };

      const renderCanvas = () => {
        const frameIndex = Math.min(count - 1, Math.max(0, Math.round(imageSequence.frame)));
        const frame = resolveRenderableFrame(frameIndex);
        if (!frame) return;
        applyCanvasBufferSize();
        const ctx = ctx2d;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const scale = Math.max(canvas.width / frame.naturalWidth, canvas.height / frame.naturalHeight);
        const drawWidth = frame.naturalWidth * scale;
        const drawHeight = frame.naturalHeight * scale;
        const drawX = (canvas.width - drawWidth) / 2;
        const drawY = (canvas.height - drawHeight) / 2;
        ctx.drawImage(frame, drawX, drawY, drawWidth, drawHeight);
        if (poster instanceof HTMLElement) {
          poster.classList.add('is-hidden');
        }
      };

      const renderFirstFrame = () => {
        imageSequence.frame = 0;
        renderCanvas();
      };

      const resizeCanvas = () => {
        renderCanvas();
      };

      for (let i = 0; i < frameLoadCount; i += 1) {
        const img = new Image();
        img.decoding = 'async';
        if (i === 0) {
          img.onload = function () {
            resizeCanvas();
            renderFirstFrame();
            scheduleNexodeAirScrollTriggerRefresh();
          };
        } else {
          img.onload = () => {
            const cur = Math.round(imageSequence.frame);
            if (Math.abs(i - cur) <= 1) {
              renderCanvas();
            }
          };
        }
        img.src = buildNexodeAirFrameUrl(prefix, i + 1, padW, ext);
        frames[i] = img;
      }

      resizeCanvas();
      renderFirstFrame();

      const onAir65WindowResize = () => {
        resizeCanvas();
        renderCanvas();
      };
      window.addEventListener('resize', onAir65WindowResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onAir65WindowResize));

      let ro = null;
      if (typeof ResizeObserver === 'function') {
        ro = new ResizeObserver(() => {
          resizeCanvas();
          renderCanvas();
        });
        ro.observe(stage);
        cleanups.push(() => {
          try {
            ro.disconnect();
          } catch (_) {}
        });
      }

      if (eyebrow instanceof HTMLElement) {
        setNexodeAirTextReveal(eyebrow, reduced ? 100 : 0);
      }

      if (!gsap || !ScrollTrigger || reduced) {
        copyLines.forEach((el) => {
          el.style.opacity = '1';
          el.style.visibility = 'visible';
          el.style.transform = 'none';
        });
        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTextReveal(eyebrow, 100);
        }
        if (copy instanceof HTMLElement) {
          copy.style.opacity = '';
          copy.style.visibility = '';
          copy.style.transform = '';
        }
        renderFirstFrame();
        return;
      }

      if (isAir65MobileFramesOnly) {
        setupAir65CopyEnter();
        return;
      }

      setupAir65CopyEnter();

      const AIR65_COPY_EXIT_Y = -48;
      const easeCopy =
        typeof gsap.parseEase === 'function' ? gsap.parseEase('power2.inOut') : (p) => p;
      let lastRenderedFrameIndex = -1;

      const pinCapFrameIndex = Math.min(AIR65_PIN_LAST_FRAME_INDEX, count - 1);
      const phase2FrameCount = Math.max(0, count - 1 - pinCapFrameIndex);

      const getAir65ScrollBase = () => {
        const vh = Math.max(
          typeof window.innerHeight === 'number' ? window.innerHeight : 0,
          pin.offsetHeight || 0,
          stage.offsetHeight || 0,
          800
        );
        const hasCopyDesktop = copyLines.length > 0 && !isAir65Mobile;
        const copyPx = hasCopyDesktop ? Math.round(vh * 0.38) : 0;
        const frameMult = isAir65Mobile ? 1.65 : 1.65;
        const countFactor = Math.min(1.55, Math.max(1, count / 100));
        const framePxFull = Math.round(vh * frameMult * countFactor);
        return { vh, hasCopyDesktop, copyPx, framePxFull };
      };

      const applyAir65CopyAndFrame = (progress, m, frameIndexMaxInclusive) => {
        const safeProgress = Math.max(0, Math.min(1, progress));
        const { copyProgressEnd, hasCopyDesktop } = m;

        if (copy instanceof HTMLElement && hasCopyDesktop) {
          const rawCopyProgress = copyProgressEnd > 0 ? safeProgress / copyProgressEnd : 1;
          const easedCopyProgress = easeCopy(Math.max(0, Math.min(1, rawCopyProgress)));
          gsap.set(copy, {
            y: AIR65_COPY_EXIT_Y * easedCopyProgress,
            autoAlpha: 1 - easedCopyProgress
          });
        } else if (copy instanceof HTMLElement) {
          gsap.set(copy, { y: 0, autoAlpha: 1 });
          if (copyLines.length) {
            gsap.set(copyLines, { autoAlpha: 1 });
          }
        }

        const frameLinear =
          copyProgressEnd > 0 && hasCopyDesktop
            ? (safeProgress - copyProgressEnd) / (1 - copyProgressEnd)
            : safeProgress;
        const frameT = Math.max(0, Math.min(1, frameLinear));
        let nextFrameIndex;
        if (frameT <= AIR65_FRAME_EPS) {
          nextFrameIndex = 0;
        } else if (frameT >= 1 - AIR65_FRAME_EPS) {
          nextFrameIndex = frameIndexMaxInclusive;
        } else {
          nextFrameIndex = Math.min(
            frameIndexMaxInclusive,
            Math.max(0, Math.round(frameIndexMaxInclusive * frameT))
          );
        }
        if (nextFrameIndex !== lastRenderedFrameIndex) {
          lastRenderedFrameIndex = nextFrameIndex;
          imageSequence.frame = nextFrameIndex;
          renderCanvas();
        }
      };

      const applyAir65Phase2FramesOnly = (progress) => {
        const safeProgress = Math.max(0, Math.min(1, progress));
        const { hasCopyDesktop } = getAir65ScrollBase();
        if (copy instanceof HTMLElement && hasCopyDesktop) {
          gsap.set(copy, { y: AIR65_COPY_EXIT_Y, autoAlpha: 0 });
        } else if (copy instanceof HTMLElement) {
          gsap.set(copy, { y: 0, autoAlpha: 1 });
          if (copyLines.length) {
            gsap.set(copyLines, { autoAlpha: 1 });
          }
        }
        let nextFrameIndex;
        if (safeProgress <= AIR65_FRAME_EPS) {
          nextFrameIndex = pinCapFrameIndex;
        } else if (safeProgress >= 1 - AIR65_FRAME_EPS) {
          nextFrameIndex = pinCapFrameIndex + phase2FrameCount;
        } else {
          nextFrameIndex = pinCapFrameIndex + Math.round(phase2FrameCount * safeProgress);
        }
        if (nextFrameIndex !== lastRenderedFrameIndex) {
          lastRenderedFrameIndex = nextFrameIndex;
          imageSequence.frame = nextFrameIndex;
          renderCanvas();
        }
      };

      if (phase2FrameCount > 0) {
        const getPhase1Metrics = () => {
          const { vh, hasCopyDesktop, copyPx, framePxFull } = getAir65ScrollBase();
          const framePx1 = Math.round((framePxFull * (pinCapFrameIndex + 1)) / count);
          const totalPx = Math.max(copyPx + framePx1, Math.round(vh * 1.25));
          const copyProgressEnd = totalPx > 0 && hasCopyDesktop ? copyPx / totalPx : 0;
          return { totalPx, copyProgressEnd, hasCopyDesktop };
        };

        const getPhase2Px = () => {
          const { framePxFull } = getAir65ScrollBase();
          return Math.round((framePxFull * phase2FrameCount) / count);
        };

        const runPhase1 = (self) => {
          applyAir65CopyAndFrame(self.progress, getPhase1Metrics(), pinCapFrameIndex);
        };

        const air65PinTrigger = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: () => '+=' + getPhase1Metrics().totalPx,
          pin,
          pinSpacing: true,
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: runPhase1,
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            runPhase1(self);
          }
        });

        const runPhase2 = (self) => {
          const sy = window.scrollY || window.pageYOffset || 0;
          if (air65PinTrigger && sy < air65PinTrigger.end - 0.5) {
            runPhase1(air65PinTrigger);
            return;
          }
          applyAir65Phase2FramesOnly(self.progress);
        };

        const air65TailTrigger = ScrollTrigger.create({
          trigger: section,
          start: () => (air65PinTrigger.end || 0),
          end: () => '+=' + getPhase2Px(),
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: runPhase2,
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            runPhase2(self);
          }
        });

        runPhase1({ progress: 0 });
        window.requestAnimationFrame(() => {
          renderCanvas();
          if (air65PinTrigger) {
            runPhase1(air65PinTrigger);
          }
          if (air65TailTrigger && air65PinTrigger && air65PinTrigger.progress >= 1) {
            runPhase2(air65TailTrigger);
          }
        });
        if (air65PinTrigger) registered.push(air65PinTrigger);
        if (air65TailTrigger) registered.push(air65TailTrigger);
      } else {
        const getAir65ScrollMetrics = () => {
          const { vh, hasCopyDesktop, copyPx, framePxFull } = getAir65ScrollBase();
          const totalPx = Math.max(copyPx + framePxFull, Math.round(vh * 1.25));
          const copyProgressEnd = totalPx > 0 && hasCopyDesktop ? copyPx / totalPx : 0;
          return { totalPx, copyProgressEnd, hasCopyDesktop };
        };

        const updateAir65State = (progress) => {
          applyAir65CopyAndFrame(progress, getAir65ScrollMetrics(), count - 1);
        };

        updateAir65State(0);

        const air65Trigger = ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: () => '+=' + getAir65ScrollMetrics().totalPx,
          pin,
          pinSpacing: true,
          scrub: AIR65_SCRUB,
          anticipatePin: 0,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            updateAir65State(self.progress);
          },
          onRefreshInit: resizeCanvas,
          onRefresh: (self) => {
            renderCanvas();
            updateAir65State(self.progress);
          }
        });

        window.requestAnimationFrame(() => {
          updateAir65State(air65Trigger ? air65Trigger.progress || 0 : 0);
        });
        if (air65Trigger) {
          registered.push(air65Trigger);
        }
      }
    });
  }

  /**
   * 色板区：Tab 指示条跟 active tab 几何同步（滚动条、ResizeObserver、窗口 resize）；
   * 切换 Tab 时用 clipPath + 透明度做左右图组过渡，token/abort 防连点叠动画；首屏眉题可走 `setNexodeAirTextReveal` 时间线。
   */
  function initNexodeAirColorCompat(root, ctx) {
    const { cleanups, registered, reduced } = ctx;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const colorSwitchTokenBySection = new WeakMap();
    const colorAnimAbortBySection = new WeakMap();

    root.querySelectorAll('[data-na-colors]').forEach((section) => {
      if (!(section instanceof HTMLElement)) return;
      const pairs = section.querySelectorAll('[data-na-colors-pair]');
      const tabs = section.querySelectorAll('[data-na-colors-tab]');
      const tabsContainer = section.querySelector('.ug-nexode-air__colors-tabs');
      const indicator = section.querySelector('[data-na-colors-indicator]');
      const prevBtn = section.querySelector('[data-na-colors-prev]');
      const nextBtn = section.querySelector('[data-na-colors-next]');
      const eyebrow = section.querySelector('[data-na-colors-eyebrow]');
      const tabsOuter = section.querySelector('[data-na-colors-tabs-outer]');
      const titleLines = section.querySelectorAll('[data-na-colors-title-line]');

      if (!tabs.length || !pairs.length) {
        return;
      }
      if (pairs.length !== tabs.length) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[nexode-air] colors: tab 与左右图组数量不一致，已跳过多色交互', {
            tabs: tabs.length,
            pairs: pairs.length
          });
        }
        section.setAttribute('data-na-colors-init-skip', 'count-mismatch');
        return;
      }
      section.removeAttribute('data-na-colors-init-skip');

      const getIsMobile = () =>
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;

      const resolveTabActiveBg = (btn) => {
        if (!(btn instanceof HTMLElement)) return '#858694';
        return (btn.dataset.activeBg || '').trim() || '#858694';
      };

      const resolveTabActiveFg = (btn) => {
        if (!(btn instanceof HTMLElement)) return '#ffffff';
        const fg = (btn.dataset.activeColor || '').trim();
        if (fg) return fg;
        return nexodeAirTabLabelColorForBg(resolveTabActiveBg(btn));
      };

      const applyTabVisual = (activeBtn) => {
        tabs.forEach((t) => {
          if (!(t instanceof HTMLElement)) return;
          const active = t === activeBtn;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          const bg = resolveTabActiveBg(t);
          if (active) {
            t.style.backgroundColor = '';
            t.style.color = resolveTabActiveFg(t);
          } else {
            t.style.backgroundColor = '';
            t.style.color = '';
          }
        });
        if (indicator instanceof HTMLElement && activeBtn instanceof HTMLElement) {
          indicator.style.backgroundColor = resolveTabActiveBg(activeBtn);
        }
      };

      const syncIndicator = (activeBtn, animate) => {
        if (!(tabsContainer instanceof HTMLElement) || !(indicator instanceof HTMLElement) || !(activeBtn instanceof HTMLElement)) {
          return;
        }
        const p = tabsContainer.getBoundingClientRect();
        const b = activeBtn.getBoundingClientRect();
        const left = b.left - p.left + tabsContainer.scrollLeft;
        const rawTop = b.top - p.top + tabsContainer.scrollTop;
        const top = rawTop - 1;
        const w = b.width;
        const h = b.height;
        if (animate && gsap && !reduced) {
          gsap.to(indicator, {
            left: left,
            top: top,
            width: w,
            height: h,
            duration: 0.4,
            ease: 'power3.out'
          });
        } else if (gsap) {
          gsap.set(indicator, { left: left, top: top, width: w, height: h });
        } else {
          indicator.style.left = left + 'px';
          indicator.style.top = top + 'px';
          indicator.style.width = w + 'px';
          indicator.style.height = h + 'px';
        }
      };

      const scheduleIndicatorSync = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
        if (cur instanceof HTMLElement) {
          syncIndicator(cur, false);
        }
      };

      const getActiveTabIndex = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        const idx = cur ? Array.prototype.indexOf.call(tabs, cur) : 0;
        return Math.max(0, Math.min(tabs.length - 1, idx));
      };

      const updateNavButtons = () => {
        const showNav = tabs.length > 1;
        const activeIdx = getActiveTabIndex();
        const showPrev = showNav && activeIdx > 0;
        const showNext = showNav && activeIdx < tabs.length - 1;
        if (prevBtn instanceof HTMLButtonElement) {
          prevBtn.hidden = !showPrev;
        }
        if (nextBtn instanceof HTMLButtonElement) {
          nextBtn.hidden = !showNext;
        }
      };

      const syncColorsTabsUi = (activeBtn, animate) => {
        updateNavButtons();
        const targetBtn =
          activeBtn instanceof HTMLElement
            ? activeBtn
            : section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
        window.requestAnimationFrame(() => {
          if (targetBtn instanceof HTMLElement) {
            syncIndicator(targetBtn, !!animate);
          }
        });
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          syncColorsTabsUi(activeTab instanceof HTMLElement ? activeTab : tabs[0], false);
        });
      });

      const activeTab = section.querySelector('[data-na-colors-tab].is-active') || tabs[0];
      if (activeTab instanceof HTMLElement) {
        applyTabVisual(activeTab);
      }
      syncColorsTabsUi(activeTab instanceof HTMLElement ? activeTab : tabs[0], false);

      const onTabsScroll = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        if (cur instanceof HTMLElement) {
          syncIndicator(cur, false);
        }
      };
      if (tabsContainer instanceof HTMLElement) {
        tabsContainer.addEventListener('scroll', onTabsScroll, { passive: true });
        cleanups.push(() => tabsContainer.removeEventListener('scroll', onTabsScroll));
      }

      let tabRo = null;
      if (typeof ResizeObserver !== 'undefined' && tabsContainer instanceof HTMLElement) {
        tabRo = new ResizeObserver(() => {
          const cur = section.querySelector('[data-na-colors-tab].is-active');
          if (cur instanceof HTMLElement) {
            syncIndicator(cur, false);
          }
        });
        tabRo.observe(tabsContainer);
        cleanups.push(() => {
          try {
            tabRo.disconnect();
          } catch (_) {}
        });
      }

      const onWinResize = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        syncColorsTabsUi(cur instanceof HTMLElement ? cur : tabs[0], false);
        if (!colorsIntroPlayed && getIsMobile() !== lastIntroIsMobile) {
          resetIntroMediaState();
        }
      };
      window.addEventListener('resize', onWinResize, { passive: true });
      cleanups.push(() => window.removeEventListener('resize', onWinResize));

      const getActiveSlideElements = () => {
        const cur = section.querySelector('[data-na-colors-tab].is-active');
        const idx = cur ? Array.prototype.indexOf.call(tabs, cur) : 0;
        const safe = Math.max(0, Math.min(tabs.length - 1, idx));
        const pair = pairs[safe];
        if (!(pair instanceof HTMLElement)) {
          return { left: null, right: null };
        }
        const left = pair.querySelector('.ug-nexode-air__colors-img-crop--left');
        const right = pair.querySelector('.ug-nexode-air__colors-right-tile');
        return { left, right };
      };

      const setStaticFinal = () => {
        if (eyebrow instanceof HTMLElement) {
          setNexodeAirTextReveal(eyebrow, 100);
          eyebrow.style.opacity = '1';
          eyebrow.style.transform = 'none';
        }
        titleLines.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.opacity = '1';
            el.style.transform = 'none';
          }
        });
        pairs.forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.opacity = '';
            el.style.transform = '';
            el.style.removeProperty('z-index');
          }
        });
        if (tabsOuter instanceof HTMLElement) {
          tabsOuter.style.opacity = '1';
          tabsOuter.style.transform = 'none';
        }
        scheduleIndicatorSync();
      };

      const runTabSwitchAnimation = (btn) => {
        let prevActive = section.querySelector('[data-na-colors-tab].is-active');
        if (!(prevActive instanceof HTMLElement)) {
          prevActive = tabs[0];
        }
        const fromIdx = Array.prototype.indexOf.call(tabs, prevActive);
        const toIdx = Array.prototype.indexOf.call(tabs, btn);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) {
          return;
        }

        const outPair = pairs[fromIdx];
        const inPair = pairs[toIdx];
        if (!(outPair instanceof HTMLElement) || !(inPair instanceof HTMLElement)) {
          return;
        }

        const outLeft = outPair.querySelector('.ug-nexode-air__colors-img-crop--left');
        const outRight = outPair.querySelector('.ug-nexode-air__colors-right-tile');
        const inLeft = inPair.querySelector('.ug-nexode-air__colors-img-crop--left');
        const inRight = inPair.querySelector('.ug-nexode-air__colors-right-tile');
        if (
          !(outLeft instanceof HTMLElement) ||
          !(outRight instanceof HTMLElement) ||
          !(inLeft instanceof HTMLElement) ||
          !(inRight instanceof HTMLElement)
        ) {
          return;
        }

        applyTabVisual(btn);
        syncColorsTabsUi(btn, true);

        if (reduced || !gsap) {
          outPair.classList.remove('is-active', 'current');
          inPair.classList.add('is-active', 'current');
          outPair.setAttribute('aria-hidden', 'true');
          inPair.setAttribute('aria-hidden', 'false');
          return;
        }

        const prevAbort = colorAnimAbortBySection.get(section);
        if (typeof prevAbort === 'function') {
          try {
            prevAbort();
          } catch (_) {}
        }

        const switchToken = (colorSwitchTokenBySection.get(section) || 0) + 1;
        colorSwitchTokenBySection.set(section, switchToken);

        gsap.killTweensOf([outPair, inPair, outLeft, outRight, inLeft, inRight]);

        const SWITCH_MS = 800;
        const switchSec = SWITCH_MS / 1000;

        pairs.forEach((el) => {
          if (el instanceof HTMLElement) el.style.removeProperty('z-index');
        });
        outPair.style.zIndex = '3';
        inPair.style.zIndex = '2';
        outPair.classList.remove('is-active');
        outPair.classList.add('current');
        inPair.classList.add('is-active', 'current');
        inPair.setAttribute('aria-hidden', 'false');

        gsap.set(inPair, { autoAlpha: 1 });
        gsap.set(outPair, { autoAlpha: 1 });
        gsap.set(inLeft, { autoAlpha: 1 });
        gsap.set(inRight, { autoAlpha: 1 });
        gsap.set(outRight, { autoAlpha: 1 });

        const outLeftRect = outLeft.getBoundingClientRect();
        const targetSize = 0;
        const targetVisibleWidth = Math.min(targetSize, outLeftRect.width);
        const targetVisibleHeight = Math.min(targetSize, outLeftRect.height);
        const maxInsetX = Math.max(0, (outLeftRect.width - targetVisibleWidth) / 2);
        const maxInsetY = Math.max(0, (outLeftRect.height - targetVisibleHeight) / 2);
        const clipState = { progress: 0 };
        const OUT_LEFT_FADE_START_W = 80;

        const applyOutLeftClip = (progress) => {
          const safe = Math.max(0, Math.min(1, progress));
          const insetX = maxInsetX * safe;
          const insetY = maxInsetY * safe;
          const clipValue =
            'inset(' +
            insetY.toFixed(3) +
            'px ' +
            insetX.toFixed(3) +
            'px ' +
            insetY.toFixed(3) +
            'px ' +
            insetX.toFixed(3) +
            'px round 30px)';
          outLeft.style.clipPath = clipValue;
          outLeft.style.webkitClipPath = clipValue;
          const visibleW = Math.max(0, outLeftRect.width - 2 * insetX);
          const fadeOp =
            visibleW >= OUT_LEFT_FADE_START_W ? 1 : Math.max(0, visibleW / OUT_LEFT_FADE_START_W);
          gsap.set(outLeft, { opacity: fadeOp });
        };

        applyOutLeftClip(0);

        const finalizePairClasses = () => {
          outPair.classList.remove('is-active', 'current');
          inPair.classList.add('is-active', 'current');
          outPair.setAttribute('aria-hidden', 'true');
          inPair.setAttribute('aria-hidden', 'false');
        };

        const clearPairAnimStyles = () => {
          try {
            gsap.set([outPair, inPair, outLeft, outRight, inLeft, inRight], { clearProps: 'opacity,visibility,transform' });
          } catch (_) {}
          outLeft.style.clipPath = '';
          outLeft.style.webkitClipPath = '';
          outPair.style.removeProperty('z-index');
          inPair.style.removeProperty('z-index');
        };

        const tl = gsap.timeline({
          onComplete: () => {
            if (colorSwitchTokenBySection.get(section) !== switchToken) {
              return;
            }
            colorAnimAbortBySection.delete(section);
            finalizePairClasses();
            clearPairAnimStyles();
          }
        });

        tl.to(
          clipState,
          {
            duration: switchSec,
            progress: 1,
            ease: 'power2.inOut',
            onUpdate: () => {
              applyOutLeftClip(clipState.progress);
            }
          },
          0
        );
        tl.fromTo(
          inRight,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: switchSec,
            ease: 'power2.out'
          },
          0
        );
        tl.to(
          outRight,
          {
            autoAlpha: 0,
            duration: switchSec,
            ease: 'power2.inOut'
          },
          0
        );

        colorAnimAbortBySection.set(section, () => {
          try {
            tl.kill();
          } catch (_) {}
          try {
            gsap.killTweensOf([outPair, inPair, outLeft, outRight, inLeft, inRight, clipState]);
          } catch (_) {}
          finalizePairClasses();
          clearPairAnimStyles();
          colorAnimAbortBySection.delete(section);
        });
      };

      const onTabClick = (e) => {
        const btn = e.currentTarget;
        if (!(btn instanceof HTMLElement)) return;
        if (btn.classList.contains('is-active')) return;
        runTabSwitchAnimation(btn);
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', onTabClick);
        cleanups.push(() => tab.removeEventListener('click', onTabClick));
      });

      if (prevBtn instanceof HTMLButtonElement) {
        const onPrevClick = () => {
          const activeIdx = getActiveTabIndex();
          const prevIdx = Math.max(0, activeIdx - 1);
          const prevTab = tabs[prevIdx];
          if (!(prevTab instanceof HTMLElement)) return;
          if (!prevTab.classList.contains('is-active')) {
            runTabSwitchAnimation(prevTab);
          }
          if (typeof prevTab.scrollIntoView === 'function') {
            prevTab.scrollIntoView({
              behavior: reduced || getIsMobile() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        };
        prevBtn.addEventListener('click', onPrevClick);
        cleanups.push(() => prevBtn.removeEventListener('click', onPrevClick));
      }

      if (nextBtn instanceof HTMLButtonElement) {
        const onNextClick = () => {
          const activeIdx = getActiveTabIndex();
          const nextIdx = Math.min(tabs.length - 1, activeIdx + 1);
          const nextTab = tabs[nextIdx];
          if (!(nextTab instanceof HTMLElement)) return;
          if (!nextTab.classList.contains('is-active')) {
            runTabSwitchAnimation(nextTab);
          }
          if (typeof nextTab.scrollIntoView === 'function') {
            nextTab.scrollIntoView({
              behavior: reduced || getIsMobile() ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest'
            });
          }
        };
        nextBtn.addEventListener('click', onNextClick);
        cleanups.push(() => nextBtn.removeEventListener('click', onNextClick));
      }

      if (reduced || !gsap || !ScrollTrigger) {
        setStaticFinal();
        return;
      }

      const pairEls = getActiveSlideElements();
      const left = pairEls.left;
      const right = pairEls.right;
      const hasPairMedia = left instanceof HTMLElement && right instanceof HTMLElement;
      let colorsIntroPlayed = false;
      let lastIntroIsMobile = getIsMobile();

      setNexodeAirTextReveal(eyebrow instanceof HTMLElement ? eyebrow : null, 0);
      if (eyebrow) {
        gsap.set(eyebrow, { autoAlpha: 0, y: 20 });
      }
      if (titleLines.length) {
        gsap.set(titleLines, { autoAlpha: 0, y: 18 });
      }

      const resetIntroMediaState = () => {
        if (!hasPairMedia || !gsap || colorsIntroPlayed) return;
        const nowMobile = getIsMobile();
        lastIntroIsMobile = nowMobile;
        gsap.set([left, right], { clearProps: 'x,y' });
        if (nowMobile) {
          gsap.set(left, { y: -36, autoAlpha: 0 });
          gsap.set(right, { y: 36, autoAlpha: 0 });
        } else {
          gsap.set(left, { x: -72, autoAlpha: 0 });
          gsap.set(right, { x: 72, autoAlpha: 0 });
        }
      };

      resetIntroMediaState();

      if (tabsOuter) {
        gsap.set(tabsOuter, { autoAlpha: 0, y: 14 });
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: NA_SCROLL_REVEAL_START,
          toggleActions: 'play none none none',
          once: true,
          onEnter: () => {
            colorsIntroPlayed = true;
          }
        }
      });

      if (eyebrow) {
        tl.fromTo(
          eyebrow,
          { autoAlpha: 0, y: 20 },
          { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out' }
        );
        tl.to(
          eyebrow,
          {
            '--na-text-reveal': '100%',
            duration: 1,
            ease: 'power2.inOut'
          },
          '-=0.15'
        );
      }

      if (titleLines.length) {
        tl.to(
          titleLines,
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.14,
            ease: 'power2.out'
          },
          eyebrow ? '-=0.35' : 0
        );
      }

      if (hasPairMedia) {
        if (getIsMobile()) {
          tl.to(
            [left, right],
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.65,
              stagger: 0.1,
              ease: 'power3.out'
            },
            titleLines.length ? '-=0.25' : eyebrow ? '-=0.2' : 0
          );
        } else {
          tl.to(
            [left, right],
            {
              x: 0,
              autoAlpha: 1,
              duration: 0.7,
              stagger: 0.08,
              ease: 'power3.out'
            },
            titleLines.length ? '-=0.25' : eyebrow ? '-=0.2' : 0
          );
        }
      }

      if (tabsOuter) {
        tl.to(
          tabsOuter,
          { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' },
          '-=0.15'
        );
      }

      if (tl.scrollTrigger) {
        registered.push(tl.scrollTrigger);
      }
    });
  }

  /**
   * Travel Kit（仅桌面）：pin 内层 + scrub 驱动左栏 slides 上移；过半进度时主/次 slide 与右侧 A/B frame 交叉淡入淡出。
   * 横竖断点切换时重建 ST，同断点内只 schedule refresh。减弱动效时跳过。
   */
  function initNexodeTravelKit(root, ctx) {
    const { cleanups, reduced } = ctx;
    if (reduced) {
      return;
    }

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) {
      return;
    }

    let travelScrollTriggers = [];

    const killTravelKitScrollTriggers = () => {
      travelScrollTriggers.forEach((st) => {
        try {
          if (st && typeof st.kill === 'function') {
            st.kill();
          }
        } catch (_) {}
      });
      travelScrollTriggers = [];
    };

    const clearTravelKitInlineStyles = () => {
      root.querySelectorAll('[data-na-travel-kit]').forEach((section) => {
        const inner = section.querySelector('[data-na-travel-inner]');
        const pin = section.querySelector('[data-na-travel-pin]');
        const left = section.querySelector('.ug-nexode-air__travel-kit-pc-left');
        const slides = section.querySelector('[data-na-travel-slides]');
        const slideEls = slides ? Array.from(slides.querySelectorAll('[data-na-travel-slide]')) : [];
        const frameA = section.querySelector('[data-na-travel-frame-a]');
        const frameB = section.querySelector('[data-na-travel-frame-b]');
        const els = [inner, pin, left, slides, frameA, frameB, ...slideEls].filter((n) => n instanceof HTMLElement);
        if (els.length) {
          gsap.set(els, { clearProps: 'height,minHeight,transform,opacity' });
        }
      });
    };

    const setupTravelKitDesktop = () => {
      killTravelKitScrollTriggers();
      gsap.killTweensOf(
        root.querySelectorAll(
          '.ug-nexode-air__travel-kit-pc-left, [data-na-travel-slide], [data-na-travel-slides], [data-na-travel-frame-a], [data-na-travel-frame-b]'
        )
      );

      const isMobile =
        typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
      if (isMobile) {
        clearTravelKitInlineStyles();
        return;
      }

      root.querySelectorAll('[data-na-travel-kit]').forEach((section) => {
        if (!(section instanceof HTMLElement)) {
          return;
        }
        const inner = section.querySelector('[data-na-travel-inner]');
        const title = section.querySelector('.ug-nexode-air__travel-kit-h2');
        const pin = section.querySelector('[data-na-travel-pin]');
        const left = section.querySelector('.ug-nexode-air__travel-kit-pc-left');
        const slides = section.querySelector('[data-na-travel-slides]');
        const slideEls = slides ? slides.querySelectorAll('[data-na-travel-slide]') : [];
        const slide0 = slideEls[0];
        const slide1 = slideEls[1];
        const stack = section.querySelector('[data-na-travel-stack]');
        const frameA = section.querySelector('[data-na-travel-frame-a]');
        const frameB = section.querySelector('[data-na-travel-frame-b]');

        if (
          !(inner instanceof HTMLElement) ||
          !(pin instanceof HTMLElement) ||
          !(left instanceof HTMLElement) ||
          !(slides instanceof HTMLElement)
        ) {
          return;
        }
        if (slideEls.length < 2 || !(frameA instanceof HTMLElement) || !(frameB instanceof HTMLElement)) {
          return;
        }

        const getFrameHeight = () =>
          stack instanceof HTMLElement && stack.offsetHeight > 0 ? stack.offsetHeight : frameA.offsetHeight;

        const syncTravelKitLayout = () => {
          const nextFrameHeight = getFrameHeight();
          if (nextFrameHeight < 1) {
            return 0;
          }

          pin.style.minHeight = nextFrameHeight + 'px';
          left.style.height = nextFrameHeight + 'px';
          slideEls.forEach((slideEl) => {
            if (slideEl instanceof HTMLElement) {
              slideEl.style.minHeight = nextFrameHeight + 'px';
              slideEl.style.height = nextFrameHeight + 'px';
            }
          });

          return nextFrameHeight;
        };

        const frameHeight = syncTravelKitLayout();
        if (frameHeight < 1) {
          return;
        }

        gsap.set(slides, { y: 0 });
        gsap.set(frameA, { opacity: 1 });
        gsap.set(frameB, { opacity: 0 });

        const TRAVEL_HALF_PROGRESS = 0.5;
        const TRAVEL_TEXT_DIM_OPACITY = 0.45;
        const TRAVEL_FRAME_FADE_SEC = 0.5;

        let travelHalfPhase = /** @type {boolean | null} */ (null);

        const keepTravelTitleVisible = () => {
          if (!(title instanceof HTMLElement)) {
            return;
          }
          title.style.opacity = '1';
          title.style.visibility = 'visible';
        };

        const syncTravelKitHalfState = (self) => {
          const afterHalf = self.progress >= TRAVEL_HALF_PROGRESS;
          if (travelHalfPhase === afterHalf) {
            return;
          }
          const prevPhase = travelHalfPhase;
          travelHalfPhase = afterHalf;

          if (slide0 instanceof HTMLElement && slide1 instanceof HTMLElement) {
            if (afterHalf) {
              gsap.set(slide0, { opacity: TRAVEL_TEXT_DIM_OPACITY });
              gsap.set(slide1, { opacity: 1 });
            } else {
              gsap.set(slide0, { opacity: 1 });
              gsap.set(slide1, { opacity: TRAVEL_TEXT_DIM_OPACITY });
            }
          }

          const animateFrames = prevPhase !== null;
          gsap.killTweensOf([frameA, frameB]);
          if (animateFrames) {
            if (afterHalf) {
              gsap.to(frameA, { opacity: 0, duration: TRAVEL_FRAME_FADE_SEC, ease: 'power2.inOut' });
              gsap.to(frameB, { opacity: 1, duration: TRAVEL_FRAME_FADE_SEC, ease: 'power2.inOut' });
            } else {
              gsap.to(frameA, { opacity: 1, duration: TRAVEL_FRAME_FADE_SEC, ease: 'power2.inOut' });
              gsap.to(frameB, { opacity: 0, duration: TRAVEL_FRAME_FADE_SEC, ease: 'power2.inOut' });
            }
          } else if (afterHalf) {
            gsap.set(frameA, { opacity: 0 });
            gsap.set(frameB, { opacity: 1 });
          } else {
            gsap.set(frameA, { opacity: 1 });
            gsap.set(frameB, { opacity: 0 });
          }
        };

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: inner,
            start: 'top top',
            end: () => '+=' + Math.max(1, syncTravelKitLayout()),
            pin: inner,
            pinSpacing: true,
            scrub: 0.35,
            invalidateOnRefresh: true,
            anticipatePin: 0.5,
            onRefreshInit: syncTravelKitLayout,
            onRefresh: (self) => {
              syncTravelKitLayout();
              keepTravelTitleVisible();
              travelHalfPhase = null;
              syncTravelKitHalfState(self);
            },
            onEnter: keepTravelTitleVisible,
            onEnterBack: keepTravelTitleVisible,
            onUpdate: (self) => {
              keepTravelTitleVisible();
              syncTravelKitHalfState(self);
            }
          }
        });

        tl.to(
          slides,
          {
            y: () => -Math.max(1, syncTravelKitLayout()),
            ease: 'none',
            duration: 1
          },
          0
        );

        if (tl.scrollTrigger) {
          travelScrollTriggers.push(tl.scrollTrigger);
          travelHalfPhase = null;
          syncTravelKitHalfState(tl.scrollTrigger);
        }

        const travelImages = [frameA.querySelector('img'), frameB.querySelector('img')].filter(
          (img) => img instanceof HTMLImageElement
        );
        const onTravelMediaReady = () => {
          syncTravelKitLayout();
          scheduleNexodeAirScrollTriggerRefresh();
        };

        travelImages.forEach((img) => {
          if (!(img instanceof HTMLImageElement)) return;
          if (img.complete) return;
          img.addEventListener('load', onTravelMediaReady, { once: true });
          cleanups.push(() => img.removeEventListener('load', onTravelMediaReady));
        });

        if (typeof ResizeObserver === 'function' && stack instanceof HTMLElement) {
          const ro = new ResizeObserver(() => {
            onTravelMediaReady();
          });
          ro.observe(stack);
          cleanups.push(() => {
            try {
              ro.disconnect();
            } catch (_) {}
          });
        }
      });
    };

    setupTravelKitDesktop();

    let lastIsMobile =
      typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          const isMobile =
            typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_MQ).matches;
          if (isMobile !== lastIsMobile) {
            lastIsMobile = isMobile;
            setupTravelKitDesktop();
          }
          scheduleNexodeAirScrollTriggerRefresh();
        }, 120);
    };
    window.addEventListener('resize', onResize, { passive: true });
    cleanups.push(() => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    });
    cleanups.push(killTravelKitScrollTriggers);
  }

  /**
   * GSAP 特性区总闸：注册 ScrollTrigger、`html` 强制 `scroll-behavior: auto`（避免与 pin/scrub 冲突），
   * 顺序执行 Highlight → Air65 → Colors → Travel，返回统一 cleanup。
   */
  function initNexodeAirFeatureSections(root) {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const cleanups = [];
    const registered = [];
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (gsap && ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
      try {
        const de = document.documentElement;
        if (de && de.dataset.ugNexodeAirScrollSnapFix !== '1') {
          de.dataset.ugNexodeAirScrollSnapFix = '1';
          de.style.scrollBehavior = 'auto';
        }
      } catch (_) {}
    }

    const ctx = { cleanups, registered, reduced };

    initWhenNexode65wHighlight(root, ctx);
    initWhenNexode65wAir65(root, ctx);
    initNexodeAirColorCompat(root, ctx);
    initNexodeTravelKit(root, ctx);

    scheduleNexodeAirScrollTriggerRefresh();

    return function cleanup() {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
      registered.forEach((st) => {
        try {
          st.kill();
        } catch (_) {}
      });
    };
  }

  if (typeof window !== 'undefined') {
    window.UgNexodeAirReveal = {
      defaults: REVEAL_DEFAULTS,
      init: initNexodeAirScrollReveal,
      refresh: refreshNexodeAirScrollReveal
    };
  }

  /** 邮箱格式校验（提交前调用）。 */
  function validateEmailFormat(email) {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      String(email).toLowerCase()
    );
  }

  /**
   * Web Component：`connectedCallback` 挂载渐显、GSAP 各段、KV 双端视频、留邮、底部 Sticky、时间线规则弹窗；
   * `disconnectedCallback` 解除监听并 kill ScrollTrigger。
   */
  class NexodeAirSection extends HTMLElement {
    constructor() {
      super();
      this._onVideoResize = null;
      this._kvResizeRaf = 0;
      this._naRevealCleanup = null;
      this._naFeatureCleanup = null;
      this._naStickyCleanup = null;
      this._naTimelineRulesCleanup = null;
    }

    connectedCallback() {
      if (this.dataset.naInited === '1') {
        return;
      }
      this.dataset.naInited = '1';

      this._naRevealCleanup = initNexodeAirScrollReveal(this);
      this._naFeatureCleanup = initNexodeAirFeatureSections(this);

      this.initKvVideo();
      this.initEmailBlocks();
      this.initStickyCta();
      this.initTimelineRulesModals();
    }

    disconnectedCallback() {
      if (this._kvResizeRaf) {
        window.cancelAnimationFrame(this._kvResizeRaf);
        this._kvResizeRaf = 0;
      }
      if (this._onVideoResize) {
        window.removeEventListener('resize', this._onVideoResize);
        this._onVideoResize = null;
      }
      this.dataset.naVideoResizeBound = '0';

      if (typeof this._naRevealCleanup === 'function') {
        this._naRevealCleanup();
        this._naRevealCleanup = null;
      }

      if (typeof this._naFeatureCleanup === 'function') {
        this._naFeatureCleanup();
        this._naFeatureCleanup = null;
      }

      if (typeof this._naStickyCleanup === 'function') {
        this._naStickyCleanup();
        this._naStickyCleanup = null;
      }

      if (typeof this._naTimelineRulesCleanup === 'function') {
        this._naTimelineRulesCleanup();
        this._naTimelineRulesCleanup = null;
      }
    }

    /**
     * KV 双视频：按 MOBILE_MQ 切换 `--pc` / `--mo`；与 nexode-air-template `initKvVideo` 对齐（含 preload / naPlayBufferedInlineVideo）。
     */
    initKvVideo() {
      const posterImg = this.querySelector('.ug-nexode-air__kv-poster');
      let lastIsMobile = null;

      const sync = () => {
        const isMobile = window.matchMedia(MOBILE_MQ).matches;
        if (lastIsMobile !== null && lastIsMobile === isMobile) {
          return;
        }
        lastIsMobile = isMobile;

        const active = isMobile
          ? this.querySelector('.ug-nexode-air__kv-video--mo')
          : this.querySelector('.ug-nexode-air__kv-video--pc');
        const inactive = isMobile
          ? this.querySelector('.ug-nexode-air__kv-video--pc')
          : this.querySelector('.ug-nexode-air__kv-video--mo');

        if (inactive instanceof HTMLVideoElement) {
          try {
            inactive.pause();
            inactive.preload = 'none';
          } catch (_) {}
          inactive.classList.remove('is-visible');
        }
        if (!(active instanceof HTMLVideoElement)) {
          return;
        }

        try {
          active.preload = 'auto';
        } catch (_) {}

        const srcEl = active.querySelector('source');
        const src = srcEl && srcEl.getAttribute('src');
        if (!src || src.trim() === '') {
          return;
        }

        const tryPlay = () => {
          active.classList.add('is-visible');
          if (posterImg) {
            posterImg.classList.add('is-hidden');
          }
          naPlayBufferedInlineVideo(active);
        };

        if (active.readyState >= 3) {
          tryPlay();
        } else {
          active.addEventListener('canplay', tryPlay, { once: true });
          active.addEventListener('loadeddata', tryPlay, { once: true });
          try {
            active.load();
          } catch (_) {}
        }
      };

      const scheduleSync = () => {
        if (this._kvResizeRaf) {
          window.cancelAnimationFrame(this._kvResizeRaf);
        }
        this._kvResizeRaf = window.requestAnimationFrame(() => {
          this._kvResizeRaf = 0;
          sync();
        });
      };

      scheduleSync();

      if (this.dataset.naVideoResizeBound === '1') {
        return;
      }
      this.dataset.naVideoResizeBound = '1';
      this._onVideoResize = scheduleSync;
      window.addEventListener('resize', this._onVideoResize, { passive: true });
    }

    /**
     * 扫描 `.ug-nexode-air__email[data-na-email]` 并 `bindEmailBlock`。
     */
    initEmailBlocks() {
      const blocks = this.querySelectorAll('.ug-nexode-air__email[data-na-email]');
      blocks.forEach((block) => {
        this.bindEmailBlock(block);
      });
    }

    /**
     * 底部 Sticky：KV 底边滚出视口且 Signup 尚未进入视口时显示条；按钮 `scrollTo` Signup。
     */
    initStickyCta() {
      const bar = this.querySelector('[data-na-sticky-cta]');
      const kv = this.querySelector('.ug-nexode-air__kv');
      const signup = this.querySelector('.ug-nexode-air__signup');
      const btn = this.querySelector('[data-na-sticky-cta-btn]');

      if (!(bar instanceof HTMLElement) || !(kv instanceof HTMLElement) || !(signup instanceof HTMLElement)) {
        return;
      }

      let rafId = 0;
      let isVisible = false;

      const setStickyVisible = (nextVisible) => {
        if (isVisible === nextVisible) return;
        isVisible = nextVisible;
        bar.classList.toggle('is-active', nextVisible);
        bar.setAttribute('aria-hidden', nextVisible ? 'false' : 'true');
      };

      const syncStickyState = () => {
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        const kvRect = kv.getBoundingClientRect();
        const signupRect = signup.getBoundingClientRect();
        const leftKvViewport = kvRect.bottom <= 0;
        const reachedSignupViewport = signupRect.top <= viewportH;
        setStickyVisible(leftKvViewport && !reachedSignupViewport);
      };

      const requestSync = () => {
        if (rafId) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = 0;
          syncStickyState();
        });
      };

      const onStickyBtnClick = () => {
        const top = signup.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top, behavior: 'smooth' });
      };

      window.addEventListener('scroll', requestSync, { passive: true });
      window.addEventListener('resize', requestSync, { passive: true });
      if (btn instanceof HTMLButtonElement) {
        btn.addEventListener('click', onStickyBtnClick);
      }

      requestSync();

      this._naStickyCleanup = () => {
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
        window.removeEventListener('scroll', requestSync);
        window.removeEventListener('resize', requestSync);
        if (btn instanceof HTMLButtonElement) {
          btn.removeEventListener('click', onStickyBtnClick);
        }
      };
    }

    /**
     * `[data-na-timeline-rules-modal]`：打开/关闭、Esc、`html.ug-nexode-air--timeline-rules-open`、焦点恢复。
     */
    initTimelineRulesModals() {
      const modals = this.querySelectorAll('[data-na-timeline-rules-modal]');
      if (!modals.length) {
        return;
      }

      const disposers = [];

      modals.forEach((modal) => {
        if (!(modal instanceof HTMLElement)) {
          return;
        }
        const timeline = modal.closest('.ug-nexode-air__timeline');
        const openBtn = timeline?.querySelector('[data-na-timeline-rules-open]');
        if (!(openBtn instanceof HTMLButtonElement)) {
          return;
        }

        const closeEls = modal.querySelectorAll('[data-na-timeline-rules-close]');
        const focusableSelector =
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

        let lastFocus = null;

        const getFocusable = () =>
          Array.from(modal.querySelectorAll(focusableSelector)).filter(
            (el) =>
              el instanceof HTMLElement &&
              !el.hasAttribute('disabled') &&
              el.getAttribute('aria-hidden') !== 'true'
          );

        const open = () => {
          lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          modal.hidden = false;
          document.documentElement.classList.add('ug-nexode-air--timeline-rules-open');
          openBtn.setAttribute('aria-expanded', 'true');
          const first = getFocusable()[0];
          if (first instanceof HTMLElement) {
            window.requestAnimationFrame(() => first.focus());
          }
        };

        const close = () => {
          modal.hidden = true;
          document.documentElement.classList.remove('ug-nexode-air--timeline-rules-open');
          openBtn.setAttribute('aria-expanded', 'false');
          if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
          } else {
            openBtn.focus();
          }
          lastFocus = null;
        };

        const onOpenClick = () => open();

        const onCloseClick = () => close();

        const onDocKeydown = (e) => {
          if (e instanceof KeyboardEvent && e.key === 'Escape' && !modal.hidden) {
            e.preventDefault();
            close();
          }
        };

        openBtn.addEventListener('click', onOpenClick);
        closeEls.forEach((el) => {
          el.addEventListener('click', onCloseClick);
        });
        document.addEventListener('keydown', onDocKeydown, true);

        disposers.push(() => {
          openBtn.removeEventListener('click', onOpenClick);
          closeEls.forEach((el) => {
            el.removeEventListener('click', onCloseClick);
          });
          document.removeEventListener('keydown', onDocKeydown, true);
          if (!modal.hidden) {
            modal.hidden = true;
            document.documentElement.classList.remove('ug-nexode-air--timeline-rules-open');
            openBtn.setAttribute('aria-expanded', 'false');
          }
        });
      });

      this._naTimelineRulesCleanup = () => {
        disposers.forEach((fn) => {
          try {
            fn();
          } catch (_) {}
        });
      };
    }

    /**
     * 留邮块：校验邮箱与隐私勾选 → POST `FORM_SUBMIT_URL`（email、accepts_marketing、form_id）→ 展示成功/服务端错误 → GA4 `subscriptionEvent`。
     */
    bindEmailBlock(block) {
      if (!(block instanceof HTMLElement)) {
        return;
      }
      if (block.dataset.naEmailInited === '1') {
        return;
      }
      block.dataset.naEmailInited = '1';

      const submitBtn = block.querySelector('.ug-nexode-air__email-submit');
      const input = block.querySelector('.ug-nexode-air__email-input');
      const privacyCheckbox = block.querySelector('.ug-nexode-air__email-checkbox');
      const errorMsg = block.querySelector('.ug-nexode-air__email-error');
      const successMsg = block.querySelector('.ug-nexode-air__email-message');

      const formId = block.getAttribute('data-form-id') || '';
      const successText = block.getAttribute('data-success-text') || 'Thanks for subscribing!';
      const failText = block.getAttribute('data-fail-text') || 'Subscription failed, please try again later.';
      const privacyError = block.getAttribute('data-privacy-error') || 'Please agree to the privacy policy.';
      const emailError = block.getAttribute('data-email-error') || 'Please enter a valid email address.';

      if (!(submitBtn instanceof HTMLButtonElement) || !(input instanceof HTMLInputElement)) {
        return;
      }

      const showError = (msg) => {
        if (errorMsg instanceof HTMLElement) {
          errorMsg.textContent = msg;
          errorMsg.style.display = 'block';
        }
        if (successMsg instanceof HTMLElement) {
          successMsg.style.display = 'none';
        }
      };

      const showSuccess = (msg) => {
        if (successMsg instanceof HTMLElement) {
          successMsg.textContent = msg;
          successMsg.style.display = 'block';
        }
        if (errorMsg instanceof HTMLElement) {
          errorMsg.style.display = 'none';
        }
      };

      const trackSubscription = (isSuccess) => {
        const w = window;
        if (!w.ugGa4Track || typeof w.ugGa4Track.subscriptionEvent !== 'function') {
          return;
        }
        try {
          w.ugGa4Track.subscriptionEvent({ isSuccess, position: 'nexode-air' });
        } catch (_) {}
      };

      submitBtn.addEventListener('click', async () => {
        const emailVal = input.value.trim();

        if (!validateEmailFormat(emailVal)) {
          showError(emailError);
          return;
        }

        if (privacyCheckbox instanceof HTMLInputElement && !privacyCheckbox.checked) {
          showError(privacyError);
          return;
        }

        if (errorMsg instanceof HTMLElement) {
          errorMsg.style.display = 'none';
        }
        if (successMsg instanceof HTMLElement) {
          successMsg.style.display = 'none';
        }

        if (submitBtn.classList.contains('loading')) {
          return;
        }

        submitBtn.classList.add('loading');

        try {
          const response = await fetch(FORM_SUBMIT_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: emailVal,
              accepts_marketing:
                privacyCheckbox instanceof HTMLInputElement && privacyCheckbox.checked ? 'on' : 'off',
              form_id: formId
            })
          });

          let data = null;
          try {
            data = await response.json();
          } catch (_) {
            showError(failText);
            trackSubscription(false);
            return;
          }

          const isSuccess = data && data.status === 'success';
          if (isSuccess) {
            input.value = '';
            showSuccess(successText);
          } else {
            const serverMsg =
              (data && data.errors && data.errors.email && data.errors.email[0]) ||
              (data && data.message) ||
              failText;
            showError(serverMsg);
          }
          trackSubscription(!!isSuccess);
        } catch (_) {
          showError(failText);
          trackSubscription(false);
        } finally {
          submitBtn.classList.remove('loading');
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e instanceof KeyboardEvent && e.key === 'Enter') {
          e.preventDefault();
          submitBtn.click();
        }
      });
    }
  }

  customElements.define(TAG_NAME, NexodeAirSection);
})();
